/**
 * Create / edit a business unit.
 *
 * The four setup steps are shown as progress at the top, live as the form is filled, so the
 * person sees what is still missing before saving rather than discovering it in the list.
 *
 * Only the name is required. The signature is **step 2 and optional** — with it off the BU
 * generates no signatures at all and is locked out of the Assinaturas screen, which is why the
 * switch says so rather than just hiding the field.
 *
 * @module Pages/BUs/BUManager
 */

import { css } from '@emotion/css';
import { GLIcon } from '@group-link-one/gl-icons';
import { UTButton, UTModal, UTSelect, UTTextField } from '@group-link-one/gl-ui/utilities';
import { useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import type { Tenant, TenantBrand } from '../BUPlatformList/BUPlatformList.types';
import { SetupProgress } from '../SetupProgress/SetupProgress';
import { UseCaseSection } from '../UseCaseSection/UseCaseSection';
import { getSetupProgress } from '../../Utils/buSetupSteps';
import { TENANT_ICON_OPTIONS } from '@/Shared/brand';
import { Switch } from '@/Shared/components/Switch/Switch';

const formStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-ml);
  width: 100%;
  min-width: 0;
`;

const progressBoxStyle = css`
  padding: var(--space-m);
  border-radius: var(--radius-m);
  background: var(--color-surface-sunken);
`;

const rowStyle = css`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(220px, 100%), 1fr));
  gap: var(--space-m);
`;

const sectionStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-m);
  padding-top: var(--space-m);
  border-top: 1px solid var(--color-divider);
`;

const sectionHeaderStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-m);
`;

const sectionTextStyle = css`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const sectionTitleStyle = css`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
`;

const sectionHintStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-secondary);
  text-wrap: pretty;
`;

const spacerStyle = css`
  flex: 1;
`;

const iconGridStyle = css`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-s);
`;

const iconButtonStyle = css`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius-m);
  background: var(--color-surface);
  cursor: pointer;
`;

const iconButtonSelectedStyle = css`
  border-color: var(--color-primary);
  background: var(--color-primary-subtle);
`;

const fieldLabelStyle = css`
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-secondary);
`;

const readOnlyLineStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
  line-height: var(--line-height-normal);
  text-wrap: pretty;
`;

const errorStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-danger);
`;

const footerStyle = css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-sm);
  padding-top: var(--space-m);
  border-top: 1px solid var(--color-divider);
`;

const validationStyle = css`
  flex: 1;
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

const BRAND_OPTIONS = [
  { id: 'utilities', text: 'Utilities', value: 'utilities' },
  { id: 'dataoris', text: 'DataOris', value: 'dataoris' },
  { id: 'sonarprint', text: 'SonarPrint', value: 'sonarprint' },
];

/** Not editable — the BFF fills these in itself. */
const SIGNATURE_SENDER = 'noreply@grouplinkone.com';
const SIGNATURE_TEMPLATE = 'email-glone.html';
const SIGNATURE_ATTACHMENT = 'signature.png';

export interface BUFormValues {
  slug: string;
  name: string;
  version: string;
  orgBackendUrl: string;
  iconName: string | null;
  flagsmithEnvironmentKey: string;
  brand: TenantBrand | null;
  signatureEnabled: boolean;
  signatureSubject: string;
}

export interface BUFormModalProps {
  open: boolean;
  /** The BU being edited, or `undefined` when creating. */
  tenant?: Tenant;
  isSaving: boolean;
  error?: string | null;
  onClose: () => void;
  onSubmit: (values: BUFormValues) => void;
}

const EMPTY: BUFormValues = {
  slug: '',
  name: '',
  version: '',
  orgBackendUrl: '',
  iconName: null,
  flagsmithEnvironmentKey: '',
  brand: null,
  signatureEnabled: false,
  signatureSubject: '',
};

/** Accepts `1.0` and `2.11.0`. */
const VERSION_PATTERN = /^\d+\.\d+(\.\d+)?$/;
const SLUG_PATTERN = /^[a-z][a-z0-9_-]{2,}$/;

export const BUFormModal = ({
  open,
  tenant,
  isSaving,
  error,
  onClose,
  onSubmit,
}: BUFormModalProps): JSX.Element => {
  const isEditing = tenant !== undefined;
  const [values, setValues] = useState<BUFormValues>(EMPTY);

  useEffect(() => {
    if (!open) return;

    setValues(
      tenant
        ? {
            slug: tenant.slug,
            name: tenant.name ?? '',
            version: tenant.version,
            orgBackendUrl: tenant.orgBackendUrl ?? '',
            iconName: tenant.iconName ?? null,
            flagsmithEnvironmentKey: tenant.flagsmithEnvironmentKey ?? '',
            brand: tenant.brand ?? null,
            signatureEnabled: Boolean(tenant.signatureSubject ?? tenant.signatureConfig),
            signatureSubject: tenant.signatureSubject ?? tenant.signatureConfig?.subject ?? '',
          }
        : EMPTY,
    );
  }, [open, tenant]);

  /** Progress computed from what is typed, not from what is saved. */
  const progress = useMemo(
    () =>
      getSetupProgress({
        ...(tenant ?? ({} as Tenant)),
        name: values.name,
        iconName: values.iconName,
        flagsmithEnvironmentKey: values.flagsmithEnvironmentKey,
        signatureSubject: values.signatureEnabled ? values.signatureSubject : null,
        signatureConfig: null,
        setupSteps: undefined,
      }),
    [tenant, values],
  );

  const isSlugValid = isEditing || SLUG_PATTERN.test(values.slug);
  const isVersionValid = isEditing || VERSION_PATTERN.test(values.version);
  const isSignatureValid = !values.signatureEnabled || values.signatureSubject.trim().length > 0;

  const missing: string[] = [];
  if (!isSlugValid) missing.push('um slug válido');
  if (!values.name.trim()) missing.push('o nome');
  if (!isVersionValid) missing.push('uma versão como 1.0 ou 2.11.0');
  if (!isSignatureValid) missing.push('o assunto do e-mail');

  const isValid = missing.length === 0;

  const content = (
    <div className={formStyle}>
      <div className={progressBoxStyle}>
        <SetupProgress progress={progress} showStepLabels showPending />
      </div>

      {!isEditing ? (
        <UTTextField
          id='bu-slug'
          topLabel='Slug'
          required
          value={values.slug}
          placeholder='water-platform'
          state={values.slug.length > 0 && !isSlugValid ? 'error' : 'normal'}
          bottomLabel={
            values.slug.length > 0 && !isSlugValid
              ? 'Minúsculas, começando por letra, com 3 caracteres ou mais.'
              : 'Não muda depois de criado.'
          }
          onChange={event => {
            setValues(current => ({ ...current, slug: event.target.value }));
          }}
        />
      ) : null}

      <UTTextField
        id='bu-name'
        topLabel='Nome exibido'
        required
        value={values.name}
        placeholder='Water Platform'
        onChange={event => {
          setValues(current => ({ ...current, name: event.target.value }));
        }}
      />

      <div className={rowStyle}>
        <UTTextField
          id='bu-version'
          topLabel='Versão inicial'
          required={!isEditing}
          disabled={isEditing}
          value={values.version}
          placeholder='2.11.0'
          state={values.version.length > 0 && !isVersionValid ? 'error' : 'normal'}
          bottomLabel={values.version.length > 0 && !isVersionValid ? 'Use 1.0 ou 2.11.0.' : ''}
          onChange={event => {
            setValues(current => ({ ...current, version: event.target.value }));
          }}
        />

        <UTSelect
          options={BRAND_OPTIONS}
          canSelectMultiple={false}
          hasCheckbox={false}
          inputOptions={{
            textFieldProps: {
              topLabel: 'Dashboard',
              placeholder: 'Selecione o dashboard',
            },
          }}
          onSelectedOptionsChange={options => {
            setValues(current => ({
              ...current,
              brand: options.length > 0 ? (String(options[0].value) as TenantBrand) : null,
            }));
          }}
        />
      </div>

      <UTTextField
        id='bu-org-backend'
        topLabel='Org backend URL'
        value={values.orgBackendUrl}
        placeholder='https://api.water.grouplinkone.com'
        onChange={event => {
          setValues(current => ({ ...current, orgBackendUrl: event.target.value }));
        }}
      />

      <UTTextField
        id='bu-flag-key'
        topLabel='Key da flag (Flagsmith)'
        value={values.flagsmithEnvironmentKey}
        placeholder='ser.xxxxxxxxxxxx'
        bottomLabel='Passo 4 · sem ela a BU não pode ser segmentada no Flagsmith.'
        onChange={event => {
          setValues(current => ({ ...current, flagsmithEnvironmentKey: event.target.value }));
        }}
      />

      <div>
        <span className={fieldLabelStyle}>Ícone · passo 3</span>
        <div className={iconGridStyle} style={{ marginTop: 'var(--space-s)' }}>
          {TENANT_ICON_OPTIONS.map(icon => (
            <button
              key={icon}
              type='button'
              aria-label={icon}
              aria-pressed={values.iconName === icon}
              className={`${iconButtonStyle} ${
                values.iconName === icon ? iconButtonSelectedStyle : ''
              }`}
              onClick={() => {
                setValues(current => ({
                  ...current,
                  iconName: current.iconName === icon ? null : icon,
                }));
              }}
            >
              <GLIcon
                variant={icon}
                size={20}
                fill={
                  values.iconName === icon ? 'var(--color-primary)' : 'var(--color-text-secondary)'
                }
              />
            </button>
          ))}
        </div>
      </div>

      <div className={sectionStyle}>
        <div className={sectionHeaderStyle}>
          <div className={sectionTextStyle}>
            <span className={sectionTitleStyle}>Assinatura · passo 2 · opcional</span>
            <span className={sectionHintStyle}>
              Desligada, a BU não aparece na tela de Assinaturas e não gera assinatura para ninguém.
            </span>
          </div>
          <div className={spacerStyle} />
          <Switch
            id='bu-signature-enabled'
            size='md'
            checked={values.signatureEnabled}
            aria-label='Assinatura habilitada'
            onChange={next => {
              setValues(current => ({ ...current, signatureEnabled: next }));
            }}
          />
        </div>

        {values.signatureEnabled ? (
          <>
            <UTTextField
              id='bu-signature-subject'
              topLabel='Assunto do e-mail enviado ao funcionário'
              required
              value={values.signatureSubject}
              placeholder='Ex.: Sua assinatura de e-mail GroupLink One'
              onChange={event => {
                setValues(current => ({ ...current, signatureSubject: event.target.value }));
              }}
            />
            <span className={readOnlyLineStyle}>
              Remetente {SIGNATURE_SENDER} · template {SIGNATURE_TEMPLATE} · anexo{' '}
              {SIGNATURE_ATTACHMENT} — definidos pelo BFF, não editáveis aqui.
            </span>
          </>
        ) : null}
      </div>

      {/* Use cases attach to a tenant that already exists, so only on edit. */}
      {isEditing ? <UseCaseSection tenantSlug={tenant.slug} /> : null}

      {error ? <span className={errorStyle}>{error}</span> : null}

      <div className={footerStyle}>
        <span className={validationStyle}>{isValid ? '' : `Informe ${missing.join(', ')}.`}</span>
        <UTButton fitContent variant='tertiary' text='Cancelar' onClick={onClose} />
        <UTButton
          fitContent
          id='bu-form-submit'
          text={isSaving ? 'Salvando...' : 'Salvar'}
          disabled={!isValid || isSaving}
          onClick={() => {
            onSubmit(values);
          }}
        />
      </div>
    </div>
  );

  return (
    <UTModal
      open={open}
      onClose={onClose}
      title={isEditing ? `Editar ${tenant.name || tenant.slug}` : 'Criar business unit'}
      description={
        isEditing
          ? 'Só o nome é obrigatório. Os outros três passos destravam comportamento.'
          : 'Só o nome e o slug são obrigatórios; os outros passos podem vir depois.'
      }
      content={content}
      maxWidth='640px'
      hasButtonClose
      position='center'
    />
  );
};
